import React, { useEffect, useState } from "react";
import { ACCENT } from "./shared";
import { trackApi, trackErrorMessage } from "./trackApi";

// Artist Studio > Audience (FSD 6.3.4): who listens and where, for the period chosen above it and, optionally, one track.
// Cards for age bands, gender, top countries, top cities (only when the server has some: they need IP geolocation), device and where plays start. The server already hides every group under 50
// listeners (merged into "Other") and answers `enough: false` when the whole audience is under 50, so this component only shows
// what it is given. No individual listener is ever shown.

const CARDS = [
  { key: "age", title: "Age" },
  { key: "gender", title: "Gender" },
  { key: "countries", title: "Top countries" },
  { key: "devices", title: "Devices" },
  { key: "sources", title: "Where plays start" },
];
const CITIES_CARD = { key: "cities", title: "Top cities" };
const LABELS = { home: "Home", sound_page: "Sound page", artist_page: "Artist page", library: "Library", search: "Search", composer: "Video composer", other: "Other", mobile: "Mobile", desktop: "Desktop", tablet: "Tablet" };
const labelOf = (v) => LABELS[v] || v;

function Card({ title, rows }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid={`audience-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <h4 className="text-sm font-bold text-gray-900">{title}</h4>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">Not enough listeners in this group to show it.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {rows.map((r) => (
            <li key={r.label}>
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">{labelOf(r.label)}</span>
                <span className="font-semibold text-gray-900">{r.percent}%</span>
              </div>
              <div className="mt-1 h-2 rounded-full bg-gray-100" aria-hidden="true">
                <div className="h-2 rounded-full" style={{ width: `${Math.min(100, r.percent)}%`, backgroundColor: ACCENT }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AudienceCards({ period, trackId }) {
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ status: "loading", data: s.data, error: "" }));
    trackApi.audience(period, trackId).then(
      (data) => { if (!cancelled) setState({ status: "ready", data, error: "" }); },
      (e) => { if (!cancelled) setState({ status: "error", data: null, error: trackErrorMessage(e, "We could not load your audience. Please try again.") }); }
    );
    return () => { cancelled = true; };
  }, [period, trackId, attempt]);

  const { data } = state;
  return (
    <section className="mt-6" aria-label="Audience" data-testid="audience">
      <h3 className="text-lg font-bold text-gray-900">Audience</h3>
      {state.status === "error" && (
        <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert" data-testid="audience-error">
          {state.error} <button type="button" className="underline font-semibold" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
        </p>
      )}
      {data && !data.enough && (
        <p className="mt-3 rounded-2xl bg-[#f1f6f2] px-5 py-6 text-center text-gray-600" data-testid="audience-empty">{data.message || "Not enough listeners yet to show demographics."}</p>
      )}
      {data && data.enough && (
        <div className={state.status === "loading" ? "opacity-60" : ""} aria-busy={state.status === "loading"}>
          <p className="mt-1 text-sm text-gray-500" data-testid="audience-total">{Number(data.total_listeners).toLocaleString()} listeners. Groups under {data.min_group} listeners are not shown.</p>
          <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {(data.cities && data.cities.length ? [...CARDS.slice(0, 3), CITIES_CARD, ...CARDS.slice(3)] : CARDS).map((c) => <Card key={c.key} title={c.title} rows={data[c.key] || []} />)}
          </div>
        </div>
      )}
    </section>
  );
}
